import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
} from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import {
  FaEdit,
  FaTrash,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaPlus,
  FaFileDownload,
  FaSearch,
  FaTimes,
} from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import * as api from '~/Services/Service';

const ITENS_POR_PAGINA = 15;

const validationSchema = Yup.object().shape({
  data: Yup.string().required('Data obrigatória'),
  comemoracao: Yup.string()
    .required('Comemoração obrigatória')
    .max(30, 'Máximo 30 caracteres'),
});

const formatarData = (dataString) => {
  if (!dataString) return '';
  const d = new Date(dataString);
  if (isNaN(d.getTime())) return dataString;
  const dia = String(d.getUTCDate()).padStart(2, '0');
  const mes = String(d.getUTCMonth() + 1).padStart(2, '0');
  const ano = d.getUTCFullYear();
  return `${dia}/${mes}/${ano}`;
};

const isoParaInputDate = (dataString) => {
  if (!dataString) return '';
  const d = new Date(dataString);
  if (isNaN(d.getTime())) return '';
  const dia = String(d.getUTCDate()).padStart(2, '0');
  const mes = String(d.getUTCMonth() + 1).padStart(2, '0');
  const ano = d.getUTCFullYear();
  return `${ano}-${mes}-${dia}`;
};

const Feriados = () => {
  const [feriados, setFeriados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  const [filtroComemoracao, setFiltroComemoracao] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [feriadoEditando, setFeriadoEditando] = useState(null);
  const [confirmacao, setConfirmacao] = useState({ show: false, item: null });
  const [ordenacao, setOrdenacao] = useState({ campo: 'data', direcao: 'desc' });
  const [paginaAtual, setPaginaAtual] = useState(1);

  useEffect(() => {
    carregarFeriados('', '', '');
  }, []);

  const carregarFeriados = async (dataInicio, dataFim, comemoracao) => {
    setLoading(true);
    try {
      const resp = await api.listarFeriados(dataInicio || null, dataFim || null, comemoracao || null);
      setFeriados(resp || []);
      setPaginaAtual(1);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao carregar feriados';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltrar = () => {
    carregarFeriados(filtroDataInicio, filtroDataFim, filtroComemoracao);
  };

  const handleLimpar = () => {
    setFiltroDataInicio('');
    setFiltroDataFim('');
    setFiltroComemoracao('');
    carregarFeriados('', '', '');
  };

  const handleNovo = () => {
    setFeriadoEditando(null);
    setShowModal(true);
  };

  const handleEditar = (feriado) => {
    setFeriadoEditando(feriado);
    setShowModal(true);
  };

  const handleFecharModal = () => {
    setShowModal(false);
    setFeriadoEditando(null);
  };

  const handleSalvar = async (values, { setSubmitting }) => {
    try {
      if (feriadoEditando) {
        await api.atualizarFeriado(feriadoEditando.data, values.comemoracao);
        toast.success('Feriado atualizado com sucesso!');
      } else {
        await api.inserirFeriado(values.data, values.comemoracao);
        toast.success('Feriado incluído com sucesso!');
      }
      setShowModal(false);
      setFeriadoEditando(null);
      carregarFeriados(filtroDataInicio, filtroDataFim, filtroComemoracao);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao salvar feriado';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleExcluir = (feriado) => {
    setConfirmacao({ show: true, item: feriado });
  };

  const handleConfirmarExclusao = async (confirmado) => {
    if (!confirmado) {
      setConfirmacao({ show: false, item: null });
      return;
    }
    const item = confirmacao.item;
    setConfirmacao({ show: false, item: null });
    try {
      await api.excluirFeriado(item.data);
      toast.success('Feriado excluído com sucesso!');
      carregarFeriados(filtroDataInicio, filtroDataFim, filtroComemoracao);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao excluir feriado';
      toast.error(msg);
    }
  };

  const handleOrdenar = (campo) => {
    setOrdenacao((prev) => {
      if (prev.campo === campo) {
        return { campo, direcao: prev.direcao === 'asc' ? 'desc' : 'asc' };
      }
      return { campo, direcao: 'asc' };
    });
    setPaginaAtual(1);
  };

  const iconeOrdenacao = (campo) => {
    if (ordenacao.campo !== campo) return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
    return ordenacao.direcao === 'asc'
      ? <FaSortUp style={{ marginLeft: 4 }} />
      : <FaSortDown style={{ marginLeft: 4 }} />;
  };

  const thSortStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

  const handleExportar = () => {
    const header = 'Data;Comemoração\n';
    const rows = feriadosOrdenados
      .map((f) => `${formatarData(f.data)};${f.assunto != null ? f.assunto : ''}`)
      .join('\n');
    const csv = header + rows;
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'feriados.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const feriadosOrdenados = useMemo(() => {
    const sorted = feriados.slice().sort((a, b) => {
      if (ordenacao.campo === 'data') {
        const da = new Date(a.data);
        const db = new Date(b.data);
        return ordenacao.direcao === 'asc' ? da - db : db - da;
      }
      const valA = (a.assunto != null ? a.assunto : '').toLowerCase();
      const valB = (b.assunto != null ? b.assunto : '').toLowerCase();
      return ordenacao.direcao === 'asc'
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    });
    return sorted;
  }, [feriados, ordenacao]);

  const totalPaginas = Math.ceil(feriadosOrdenados.length / ITENS_POR_PAGINA);
  const feriadosPaginados = feriadosOrdenados.slice(
    (paginaAtual - 1) * ITENS_POR_PAGINA,
    paginaAtual * ITENS_POR_PAGINA,
  );

  const renderPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const paginas = [];
    const max = 5;
    let inicio = Math.max(1, paginaAtual - Math.floor(max / 2));
    const fim = Math.min(totalPaginas, inicio + max - 1);
    if (fim - inicio + 1 < max) inicio = Math.max(1, fim - max + 1);

    paginas.push(
      <Button
        key="first"
        size="sm"
        variant="outline-secondary"
        className="mr-1"
        disabled={paginaAtual === 1}
        onClick={() => setPaginaAtual(1)}
      >
        «
      </Button>,
      <Button
        key="prev"
        size="sm"
        variant="outline-secondary"
        className="mr-1"
        disabled={paginaAtual === 1}
        onClick={() => setPaginaAtual((p) => p - 1)}
      >
        ‹
      </Button>,
    );

    if (inicio > 1) {
      paginas.push(<span key="ellipsis-start" className="mr-1">...</span>);
    }

    for (let i = inicio; i <= fim; i++) {
      const pagina = i;
      paginas.push(
        <Button
          key={pagina}
          size="sm"
          variant={paginaAtual === pagina ? 'primary' : 'outline-secondary'}
          className="mr-1"
          onClick={() => setPaginaAtual(pagina)}
        >
          {pagina}
        </Button>,
      );
    }

    if (fim < totalPaginas) {
      paginas.push(<span key="ellipsis-end" className="mr-1">...</span>);
    }

    paginas.push(
      <Button
        key="next"
        size="sm"
        variant="outline-secondary"
        className="mr-1"
        disabled={paginaAtual === totalPaginas}
        onClick={() => setPaginaAtual((p) => p + 1)}
      >
        ›
      </Button>,
      <Button
        key="last"
        size="sm"
        variant="outline-secondary"
        disabled={paginaAtual === totalPaginas}
        onClick={() => setPaginaAtual(totalPaginas)}
      >
        »
      </Button>,
    );

    return (
      <div className="d-flex justify-content-between align-items-center px-3 py-2">
        <small className="text-muted">
          Mostrando {(paginaAtual - 1) * ITENS_POR_PAGINA + 1} até{' '}
          {Math.min(paginaAtual * ITENS_POR_PAGINA, feriadosOrdenados.length)} de{' '}
          {feriadosOrdenados.length} registros
        </small>
        <div>{paginas}</div>
      </div>
    );
  };

  if (loading) return <Loading />;

  const itemExclusao = confirmacao.item;

  return (
    <Container fluid className="px-3 py-3">
      <style>{`
        .feriados-desktop { display: block; }
        .feriados-mobile  { display: none; }
        @media (max-width: 768px) {
          .feriados-desktop { display: none !important; }
          .feriados-mobile  { display: block; }
          .feriados-filtro-row { flex-direction: column; }
          .feriados-filtro-row .text-right { text-align: right !important; margin-top: 8px; }
        }
        .feriados-modal-header .close { color: #fff; opacity: 1; }
        .feriados-modal-header .close:hover { color: #ddd; }
      `}</style>

      <Header title="Feriados" />

      <Card className="shadow-sm mb-3">
        <Card.Body className="py-2">
          <Row className="align-items-end feriados-filtro-row">
            <Col md={2}>
              <Form.Group className="mb-0">
                <Form.Label className="mb-1" style={{ fontSize: '0.85rem' }}>Data Início</Form.Label>
                <Form.Control
                  type="date"
                  value={filtroDataInicio}
                  onChange={(e) => setFiltroDataInicio(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-0">
                <Form.Label className="mb-1" style={{ fontSize: '0.85rem' }}>Data Fim</Form.Label>
                <Form.Control
                  type="date"
                  value={filtroDataFim}
                  onChange={(e) => setFiltroDataFim(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-0">
                <Form.Label className="mb-1" style={{ fontSize: '0.85rem' }}>Comemoração</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Buscar..."
                  value={filtroComemoracao}
                  onChange={(e) => setFiltroComemoracao(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleFiltrar(); }}
                />
              </Form.Group>
            </Col>
            <Col md={5} className="mt-2 mt-md-0">
              <Button variant="primary" className="mr-1" onClick={handleFiltrar}>
                <FaSearch className="mr-1" /> Buscar
              </Button>
              <Button variant="secondary" onClick={handleLimpar}>
                <FaTimes className="mr-1" /> Limpar
              </Button>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col className="text-right">
              <Button variant="success" className="mr-2" onClick={handleNovo}>
                <FaPlus className="mr-1" /> Novo Feriado
              </Button>
              <Button variant="outline-secondary" onClick={handleExportar}>
                <FaFileDownload className="mr-1" /> Exportar CSV
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm mx-auto" style={{ maxWidth: '600px' }}>
        <Card.Header
          className="d-flex justify-content-between align-items-center py-2"
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
        >
          <strong>
            Feriados cadastrados
            <span className="ml-2" style={{ fontSize: '0.85rem', opacity: 0.8 }}>
              ({feriadosOrdenados.length} {feriadosOrdenados.length === 1 ? 'registro' : 'registros'})
            </span>
          </strong>
        </Card.Header>
        <Card.Body className="p-0">
          {feriadosOrdenados.length === 0 ? (
            <p className="text-muted text-center my-4">Nenhum feriado cadastrado</p>
          ) : (
            <>
              {/* Desktop */}
              <div className="feriados-desktop">
                <Table striped bordered hover size="sm" className="mb-0" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th
                        style={Object.assign({ width: '110px' }, thSortStyle)}
                        onClick={() => handleOrdenar('data')}
                      >
                        Data{iconeOrdenacao('data')}
                      </th>
                      <th
                        style={thSortStyle}
                        onClick={() => handleOrdenar('assunto')}
                      >
                        Comemoração{iconeOrdenacao('assunto')}
                      </th>
                      <th style={{ width: '90px', textAlign: 'center' }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feriadosPaginados.map((item) => (
                      <tr key={item.data}>
                        <td>{formatarData(item.data)}</td>
                        <td>{item.assunto}</td>
                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            title="Editar"
                            className="mr-1"
                            onClick={() => handleEditar(item)}
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            title="Excluir"
                            onClick={() => handleExcluir(item)}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Mobile */}
              <div className="feriados-mobile p-2">
                {feriadosPaginados.map((item) => (
                  <Card key={item.data} className="mb-2 shadow-sm">
                    <Card.Body className="py-2 px-3">
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <div>
                          <strong style={{ fontSize: '0.95rem' }}>{item.assunto}</strong>
                          <br />
                          <small className="text-muted">{formatarData(item.data)}</small>
                        </div>
                        <div>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="mr-1"
                            onClick={() => handleEditar(item)}
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleExcluir(item)}
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>

              {renderPaginacao()}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modal Inclusão/Edição */}
      <Modal show={showModal} onHide={handleFecharModal} centered backdrop="static">
        <Modal.Header
          closeButton
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
          className="feriados-modal-header"
        >
          <Modal.Title style={{ fontSize: '1rem', color: '#fff' }}>
            {feriadoEditando ? 'Editar Feriado' : 'Novo Feriado'}
          </Modal.Title>
        </Modal.Header>
        <Formik
          enableReinitialize
          initialValues={{
            data: feriadoEditando ? isoParaInputDate(feriadoEditando.data) : '',
            comemoracao: feriadoEditando ? (feriadoEditando.assunto != null ? feriadoEditando.assunto : '') : '',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSalvar}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
            <Form onSubmit={handleSubmit}>
              <Modal.Body>
                <Form.Group>
                  <Form.Label>Data <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    name="data"
                    value={values.data}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={!!feriadoEditando}
                    isInvalid={touched.data && !!errors.data}
                    style={{ maxWidth: '180px' }}
                  />
                  <Form.Control.Feedback type="invalid">{errors.data}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Comemoração <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    name="comemoracao"
                    value={values.comemoracao}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={30}
                    placeholder="Nome do feriado"
                    isInvalid={touched.comemoracao && !!errors.comemoracao}
                    autoFocus={!feriadoEditando}
                    style={{ maxWidth: '300px' }}
                  />
                  <Form.Control.Feedback type="invalid">{errors.comemoracao}</Form.Control.Feedback>
                  <Form.Text className="text-muted">{values.comemoracao.length}/30 caracteres</Form.Text>
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleFecharModal} disabled={isSubmitting}>
                  Cancelar
                </Button>
                <Button variant="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Salvando...' : 'Salvar'}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* Modal Confirmação Exclusão */}
      <ModalConfirmacao
        show={confirmacao.show}
        title="Confirmar Exclusão"
        message={
          itemExclusao
            ? `Deseja excluir o feriado ${formatarData(itemExclusao.data)} - ${itemExclusao.assunto != null ? itemExclusao.assunto : ''}?`
            : ''
        }
        onClose={handleConfirmarExclusao}
      />
    </Container>
  );
};

export default Feriados;
